import { Card, ClickAwayListener, InputAdornment } from "@mui/material";
import TextField from "@mui/material/TextField";
import { useEffect, useState } from "react";
import DateRange from "@mui/icons-material/DateRange";
import DatePickerCalendar from "./DatePickerCalendar";
import { formatDateToMMDDYYYY, isValidDate } from "utils/date";

interface DatePickerDateFieldProps {
	label: string;
	value?: Date | null;
	firstValidDate?: Date;
	secondValidDate?: Date;
	allowFutureDates?: boolean;
	allowCurrentValueOutsideSelectableDates?: boolean;
	restrictInputToSelectableDates?: boolean;
	onChange: (date: Date) => void;
	onInvalidSelectableDate?: (date: Date) => void;
	valueCanBeNull?: boolean;
	isEndIcon?: boolean;
	containerPadding?: number | string;
}

const DatePickerDateField = ({
	label,
	value,
	firstValidDate,
	secondValidDate,
	allowFutureDates = false,
	allowCurrentValueOutsideSelectableDates = false,
	restrictInputToSelectableDates = false,
	onChange,
	onInvalidSelectableDate,
	valueCanBeNull,
	isEndIcon = false,
	containerPadding = "10px 0 10px 0"
}: DatePickerDateFieldProps) => {
	const displayValueAsNull = valueCanBeNull && value == null;
	const [dateValue, setDateValue] = useState<Date | null>(
		displayValueAsNull ? null : (value ?? firstValidDate ?? new Date())
	);
	const [showCalendar, setShowCalendar] = useState<boolean>(false);
	const [dateInputText, setDateInputText] = useState(
		!displayValueAsNull && dateValue ? formatDateToMMDDYYYY(dateValue) : ""
	);

	useEffect(() => {
		if (valueCanBeNull && value == null) {
			setDateValue(null);
			setDateInputText("");
			return;
		}

		const nextDateValue = value ?? firstValidDate ?? new Date();
		setDateValue(nextDateValue);
		setDateInputText(formatDateToMMDDYYYY(nextDateValue));
	}, [firstValidDate, value, valueCanBeNull]);

	const adornmentIcon = (
		<InputAdornment
			position={isEndIcon ? "end" : "start"}
			data-testid="date-range-picker-calendar-icon"
		>
			<DateRange />
		</InputAdornment>
	);

	const getStartOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
	const isSameDate = (date1: Date, date2: Date) => getStartOfDay(date1).getTime() === getStartOfDay(date2).getTime();

	const isDateSelectable = (date: Date) => {
		if (!restrictInputToSelectableDates) return true;
		if (allowCurrentValueOutsideSelectableDates && dateValue && isSameDate(date, dateValue)) return true;

		const dateStart = getStartOfDay(date);
		const firstValidDateStart = firstValidDate ? getStartOfDay(firstValidDate) : undefined;
		let secondValidDateStart: Date | undefined;
		if (secondValidDate) {
			secondValidDateStart = getStartOfDay(secondValidDate);
		} else if (!allowFutureDates) {
			secondValidDateStart = getStartOfDay(new Date());
		}

		if (firstValidDateStart && dateStart < firstValidDateStart) return false;
		if (secondValidDateStart && dateStart > secondValidDateStart) return false;

		return true;
	};

	const handleInvalidSelectableDate = (date: Date) => {
		onInvalidSelectableDate?.(date);
	};

	const resetDateInputText = () => {
		if (displayValueAsNull) {
			setDateInputText("");
		} else if (dateValue) {
			setDateInputText(formatDateToMMDDYYYY(dateValue));
		}
	};

	const handleClickOutside = () => {
		const newDateValue = new Date(dateInputText);
		if (isValidDate(newDateValue) && isDateSelectable(newDateValue)) {
			setDateValue(newDateValue);
			setDateInputText(formatDateToMMDDYYYY(newDateValue));
			onChange(newDateValue);
		} else if (isValidDate(newDateValue)) {
			handleInvalidSelectableDate(newDateValue);
			resetDateInputText();
		} else {
			resetDateInputText();
		}
		setShowCalendar(false);
	};

	const onHandleDateChange = (date: Date) => {
		if (isValidDate(date) && isDateSelectable(date)) {
			setDateInputText(formatDateToMMDDYYYY(date));
			setDateValue(date);
			if (onChange) {
				onChange(date);
			}
			setShowCalendar(false);
		} else if (isValidDate(date)) {
			handleInvalidSelectableDate(date);
		}
	};

	const onHandleDateInputChange = (e: string) => {
		setDateInputText(e);
		const newDate = new Date(e);
		if (isValidDate(newDate) && isDateSelectable(newDate)) {
			onChange(newDate);
		} else if (isValidDate(newDate)) {
			handleInvalidSelectableDate(newDate);
		}
	};

	const fromDateInvalid = !!firstValidDate && !!secondValidDate && firstValidDate > secondValidDate;

	return (
		<div style={{ padding: containerPadding }}>
			<TextField
				onClick={() => setShowCalendar(true)}
				onChange={(e) => onHandleDateInputChange(e.target.value)}
				fullWidth
				id="date-range-picker"
				label={label}
				variant="outlined"
				size="small"
				value={dateInputText}
				error={fromDateInvalid}
				helperText={
					fromDateInvalid ? '"To" date must be later than "From" date to execute search properly.' : ""
				}
				inputProps={{ "data-testid": `${label}-date-range-picker-text-field` }}
				InputProps={{
					[isEndIcon ? "endAdornment" : "startAdornment"]: adornmentIcon
				}}
			/>
			{showCalendar && (
				<ClickAwayListener onClickAway={handleClickOutside}>
					<Card
						id={`date-picker-${label.replaceAll(" ", "")}-calendar`}
						sx={{
							mt: "1rem",
							flexDirection: "column"
						}}
					>
						<DatePickerCalendar
							value={dateValue ?? firstValidDate ?? new Date()}
							firstValidDate={firstValidDate}
							secondValidDate={secondValidDate}
							allowFutureDates={allowFutureDates}
							onChange={onHandleDateChange}
						/>
					</Card>
				</ClickAwayListener>
			)}
		</div>
	);
};

export default DatePickerDateField;
